import Seo from "@/components/feature/Seo";
import Container from "@/components/base/Container";
import PageHero from "@/components/base/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import Button from "@/components/base/Button";
import ContactForm from "./components/ContactForm";
import { useCompanyInfo } from "@/hooks/usePublicContent";

export default function Contacto() {
  const companyInfo = useCompanyInfo();

  const canales = [
  {
    id: "tel",
    icono: "ri-phone-line",
    titulo: "Teléfono",
    valor: companyInfo.telefono,
    descripcion: "Atención directa de lunes a sábado.",
    href: companyInfo.telefonoLink,
    action: "Llamar ahora",
  },
  {
    id: "mail",
    icono: "ri-mail-line",
    titulo: "Correo",
    valor: companyInfo.email,
    descripcion: "Cotizaciones y consultas formales.",
    href: companyInfo.emailLink,
    action: "Escribir correo",
  },
  {
    id: "wa",
    icono: "ri-whatsapp-line",
    titulo: "WhatsApp",
    valor: companyInfo.whatsapp,
    descripcion: "Consultas rápidas y coordinación.",
    href: companyInfo.whatsappLink,
    action: "Abrir WhatsApp",
  },
  {
    id: "dir",
    icono: "ri-map-pin-2-line",
    titulo: "Ubicación",
    valor: `${companyInfo.direccion}, ${companyInfo.ciudad}`,
    descripcion: "Sede y patio de operaciones.",
    href: "https://maps.google.com/?q=Parque+Industrial+Lima+Peru",
    action: "Ver en el mapa",
  },
];

  return (
    <>
      <Seo
        title="Contacto"
        description="Contáctate con AXONIA GROUP por teléfono, correo o WhatsApp. Atendemos cotizaciones, consultas y coordinación de servicios de transporte y mudanzas."
        path="/contacto"
      />
      <PageHero
        title="Contáctanos"
        subtitle="Estamos listos para ayudarte a mover tu carga, tu equipo o tus pertenencias. Elige el canal que prefieras."
        crumbs={[{ label: "Inicio", to: "/" }, { label: "Contacto" }]}
        image="https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20a%20modern%20logistics%20office%20reception%20with%20a%20white%20truck%20visible%20through%20large%20windows%2C%20professional%20corporate%20photography%2C%20deep%20blue%20tones%2C%20clean%20bright%20interior%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-contacto&orientation=landscape"
      />

      <section className="bg-background-50 py-12 md:py-16">
        <Container>
          <SectionHeading
            eyebrow="Canales de atención"
            title="Comunícate con AXONIA GROUP"
            description="Resolvemos tus dudas, preparamos cotizaciones y coordinamos tu servicio por el medio que te resulte más cómodo."
          />

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {canales.map((canal, index) => (
              <article
                key={canal.id}
                className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 animate-fade-up"
                style={{ animationDelay: `${index * 60}ms` }}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${canal.icono} text-xl leading-none`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 text-sm font-label font-semibold uppercase tracking-wider text-foreground-500">
                  {canal.titulo}
                </h3>
                <p className="mt-1 flex-1 text-sm font-medium text-foreground-950">{canal.valor}</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-500">{canal.descripcion}</p>
                <a
                  href={canal.href}
                  target={canal.id === "wa" || canal.id === "dir" ? "_blank" : undefined}
                  rel={canal.id === "wa" || canal.id === "dir" ? "noopener noreferrer" : undefined}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-label font-semibold text-primary-600 transition-colors duration-200 hover:text-primary-700"
                >
                  {canal.action}
                  <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true"></i>
                </a>
              </article>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between md:p-6">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-background-50 text-secondary-700">
                <i className="ri-time-line text-xl leading-none" aria-hidden="true"></i>
              </span>
              <div>
                <h3 className="text-sm font-heading font-semibold text-foreground-950">
                  Horario de atención
                </h3>
                <p className="mt-1 text-sm text-foreground-600">{companyInfo.horario}</p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  Coordinación de operaciones disponible 24/7 para servicios programados.
                </p>
              </div>
            </div>
            <Button to="/solicitar-servicio" variant="primary" size="md" icon="ri-send-plane-line">
              Solicitar servicio
            </Button>
          </div>
        </Container>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <Container>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
            <ContactForm />

            <div className="flex flex-col gap-6">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="h-64 w-full md:h-80">
                  <iframe
                    title="Ubicación de AXONIA GROUP en el mapa"
                    src={companyInfo.mapaEmbed}
                    className="h-full w-full border-0"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                <div className="flex items-start gap-3 p-5">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-map-pin-2-line text-lg leading-none" aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-950">
                      Patio de operaciones
                    </h3>
                    <p className="mt-1 text-sm text-foreground-600">
                      {companyInfo.direccion}
                      <br />
                      {companyInfo.ciudad}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="text-base font-heading font-semibold text-foreground-950">
                  Síguenos
                </h3>
                <p className="mt-1.5 text-sm text-foreground-600">
                  Conoce nuestras operaciones y novedades en redes sociales.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {companyInfo.redes.map((red) => (
                    <a
                      key={red.nombre}
                      href={red.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={red.nombre}
                      className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-900 transition-colors duration-200 hover:bg-primary-500 hover:text-background-50 dark:hover:text-foreground-950"
                    >
                      <i className={`${red.icono} text-lg leading-none`} aria-hidden="true"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}